import { HStack } from '@chakra-ui/react'
import { createColumnHelper } from '@tanstack/react-table'

import DownloadButton from '@/components/Elements/Button/DownloadButton'
import { DateCell, RawTextCell } from '@/components/Table/components/Cell'
import { FilterType } from '@/components/Table/components/Filter/TableFilter'

import DocumentTemplatePreviewButton from '../components/DocumentTemplatePreviewButton'
import {
  DOCUMENT_TEMPLATE_STATUS_LABEL,
  DOCUMENT_TEMPLATE_TEMPLATE_TYPE_LABEL,
  DOCUMENT_TEMPLATE_TYPE_LABEL,
  DOCUMENT_TEMPLATE_TYPES_WITH_SIGNED_PREVIEW,
  DocumentTemplateStatus,
  DocumentTemplateTemplateType,
} from '../documentTemplate.constant'

/**
 * No delete or activate/deactivate switch: a template is never removed, and
 * which one is "in use" is derived from its active range rather than toggled
 * by hand — see ADR document-template/0005. The per-row actions all work on
 * the uploaded file, when there is one: download it, or preview it filled
 * with sample data.
 */
const getDocumentTemplateTableColumns = () => {
  const columnHelper = createColumnHelper()

  return [
    columnHelper.accessor('templateType', {
      header: () => <span>Template Type</span>,
      size: 120,
      cell: ({ getValue }) => DOCUMENT_TEMPLATE_TEMPLATE_TYPE_LABEL[getValue()] ?? getValue(),
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.entries(DOCUMENT_TEMPLATE_TEMPLATE_TYPE_LABEL).map(([value, label]) => ({
          label,
          value,
        })),
      },
    }),

    columnHelper.accessor('type', {
      header: () => <span>Type</span>,
      size: 200,
      cell: ({ getValue }) => DOCUMENT_TEMPLATE_TYPE_LABEL[getValue()] ?? getValue(),
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.entries(DOCUMENT_TEMPLATE_TYPE_LABEL).map(([value, label]) => ({ label, value })),
      },
    }),

    columnHelper.accessor('name', {
      header: () => <span>Name</span>,
      size: 260,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.TEXT,
      },
    }),

    columnHelper.accessor('activeStartDate', {
      header: () => <span>Active Start Date</span>,
      size: 150,
      cell: DateCell,
      enableColumnFilter: false,
    }),

    columnHelper.accessor('activeEndDate', {
      header: () => <span>Active End Date</span>,
      size: 150,
      cell: DateCell,
      enableColumnFilter: false,
    }),

    columnHelper.accessor('status', {
      header: () => <span>Status</span>,
      size: 120,
      cell: ({ getValue }) => DOCUMENT_TEMPLATE_STATUS_LABEL[getValue()] ?? getValue(),
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values(DocumentTemplateStatus).map((value) => ({
          label: DOCUMENT_TEMPLATE_STATUS_LABEL[value],
          value,
        })),
      },
    }),

    columnHelper.display({
      id: 'action',
      header: () => <span>Action</span>,
      size: 340,
      enableHiding: false,
      enableColumnFilter: false,
      cell: ({ row: { original } }) =>
        original?.templateType === DocumentTemplateTemplateType.FILE && original?.file?._id ? (
          <HStack spacing="2">
            <DownloadButton fileId={original.file._id} fileName={original.file.originalFileName} />
            <DocumentTemplatePreviewButton templateId={original._id}>Preview</DocumentTemplatePreviewButton>
            {DOCUMENT_TEMPLATE_TYPES_WITH_SIGNED_PREVIEW.includes(original.type) && (
              <DocumentTemplatePreviewButton templateId={original._id} signed>
                Preview Sign
              </DocumentTemplatePreviewButton>
            )}
          </HStack>
        ) : null,
    }),
  ]
}

export default getDocumentTemplateTableColumns
